import React, { useEffect, useImperativeHandle, useState } from "react";
// import PropTypes from "prop-types";
import QuestionPreview from "../QuestionPreview";
import useStyles from "./styles";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getTranslation } from "../../../locales";

const renderMask = (format) => {
  if (format === "yyyy/mm/dd") {
    return "____/__/__";
  } else if (format === "yyyy-mm-dd") {
    return "____-__-__";
  } else {
    return "__/__/____";
  }
};

const AnswerContact = (props, ref) => {
  const { t } = useTranslation(["surveyBuilder", "common"]);

  const styles = useStyles(props);

  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    setQuestions(
      props.value?.answers?.length > 0
        ? props.value.answers
        : props.contactForms || []
    );
  }, [props.value]);

  const checkError = (value, required, fieldType) => {
    const onlyCharactersRegex = /^[a-zA-Z\s]*$/;
    const zipCodeRegex = /^\d{5}$|^\d{5}-\d{4}$/;
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    const emailRegex =
      /^[a-zA-Z0-9_.+-]+@(?:(?:[a-zA-Z0-9-]+\.)?[a-zA-Z0-9-]+\.)?(\w{2,3})$/;

    const hasValue =
      fieldType === "dateOfBirth" ? !!value : value?.trim().length > 0;

    if (required && !hasValue) {
      return t("common:validationErrors.thisFieldIsRequired");
    } else if (hasValue) {
      switch (fieldType) {
        case "dateOfBirth":
          if (value === "Invalid date") {
            return t("common:validationErrors.invalidDate");
          }
          break;
        case "name":
          if (!onlyCharactersRegex.test(value)) {
            return t("common:validationErrors.noSymbolIsAllowed");
          }
          break;

        case "email":
          if (!emailRegex.test(value)) {
            return t("common:validationErrors.pleaseEnterValidEmail");
          }
          break;

        case "phone":
          if (!phoneRegex.test(value)) {
            return t("common:validationErrors.phoneNumberIsNotValid");
          }
          break;

        case "zip":
          if (!zipCodeRegex.test(value)) {
            return t("common:validationErrors.zipCodeIsNotValid");
          }
          break;
      }
    }
    return null;
  };

  useImperativeHandle(ref, () => ({
    validate: (data) => {
      if (!data) {
        return !questions.some(({ required, fieldType }, index) => {
          const hasError = !!checkError("", required, fieldType);
          if (hasError) {
            onChange(index, null, "", required, fieldType);
          }
          return hasError;
        });
      }
      return !data?.answers?.some(
        (
          { value, errorMessage, required, fieldType, nameFieldType },
          index
        ) => {
          let hasErrorMessage;
          if (errorMessage && typeof errorMessage === "object") {
            const errorMessageArray = Object.entries(errorMessage);
            hasErrorMessage = errorMessageArray.some(([, v]) => !!v);
          } else {
            hasErrorMessage = !!errorMessage;
          }

          if (!hasErrorMessage && required) {
            if (value && typeof value === "object") {
              if (fieldType === "name") {
                if (nameFieldType === 2) {
                  value = {
                    firstName: "",
                    lastName: "",
                    ...value,
                  };
                }
                if (nameFieldType === 3) {
                  value = {
                    firstName: "",
                    lastName: "",
                    middleName: "",
                    ...value,
                  };
                }
              }
              const valueArray = Object.entries(value);

              if (fieldType === "name" && nameFieldType !== 1) {
                hasErrorMessage = valueArray.length !== nameFieldType;
              }

              if (!hasErrorMessage) {
                hasErrorMessage = valueArray.some(
                  ([, v]) => !!checkError(v, required, fieldType)
                );
              }
              if (hasErrorMessage) {
                valueArray.forEach(([k, v]) => {
                  onChange(index, k, v, required, fieldType);
                });
              }
            } else {
              hasErrorMessage = !!checkError(value, required, fieldType);
            }
          }
          return hasErrorMessage;
        }
      );
    },
  }));

  const onChange = (index, key, value, required, fieldType) => {
    const updatedQuestions = [...questions];

    const errorMessage = checkError(value, required, fieldType);

    if (key) {
      updatedQuestions[index].errorMessage = {
        ...(typeof updatedQuestions[index].errorMessage !== "object"
          ? {}
          : updatedQuestions[index].errorMessage),
        [key]: errorMessage,
      };
      updatedQuestions[index].value = {
        ...(typeof updatedQuestions[index].value !== "object"
          ? {}
          : updatedQuestions[index].value),
        [key]: value,
      };
    } else {
      updatedQuestions[index].errorMessage = errorMessage;
      updatedQuestions[index].value = value;
    }

    setQuestions(updatedQuestions);

    props.handleChange({
      questionType: props.type,
      answers: updatedQuestions,
    });
  };

  return (
    <div className={styles.questionWrapper}>
      <QuestionPreview
        question={props.question}
        required={props.required}
        helperText={props.helperText}
      />
      <div className={styles.answer}>
        <Grid container>
          {questions.map((question, index) => {
            const {
              fieldType,
              nameFieldType,
              required,
              label,
              inputType,
              dropdownContent,
              dateFormat,
              maskTextInput,
              errorMessage,
              value,
            } = question;

            switch (fieldType) {
              case "name":
                switch (nameFieldType) {
                  case 1:
                    return (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={getTranslation(
                            props.currentLang,
                            "surveyBuilder:questionContact.fullName"
                          )}
                          variant="outlined"
                          className={styles.oneRowNameInput}
                          placeholder={getTranslation(
                            props.currentLang,
                            "surveyBuilder:questionContact.fullName"
                          )}
                          inputProps={{
                            "data-testid": "fullName",
                          }}
                          InputLabelProps={{
                            required: required,
                          }}
                          onChange={(e) =>
                            onChange(
                              index,
                              null,
                              e.target.value,
                              required,
                              fieldType
                            )
                          }
                          onBlur={(e) =>
                            onChange(
                              index,
                              null,
                              e.target.value,
                              required,
                              fieldType
                            )
                          }
                          error={!!errorMessage}
                          helperText={errorMessage}
                          value={value}
                        />
                      </Grid>
                    );

                  case 2:
                    return (
                      <>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label={getTranslation(
                              props.currentLang,
                              "surveyBuilder:questionContact.firstName"
                            )}
                            variant="outlined"
                            className={styles.oneRowNameInput}
                            inputProps={{
                              "data-testid": "firstName",
                            }}
                            InputLabelProps={{
                              required: required,
                            }}
                            onChange={(e) =>
                              onChange(
                                index,
                                "firstName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            onBlur={(e) =>
                              onChange(
                                index,
                                "firstName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            error={!!errorMessage?.firstName}
                            helperText={errorMessage?.firstName}
                            value={value?.firstName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label={getTranslation(
                              props.currentLang,
                              "surveyBuilder:questionContact.lastName"
                            )}
                            variant="outlined"
                            className={styles.oneRowNameInput}
                            inputProps={{
                              "data-testid": "lastName",
                            }}
                            InputLabelProps={{
                              required: required,
                            }}
                            onChange={(e) =>
                              onChange(
                                index,
                                "lastName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            onBlur={(e) =>
                              onChange(
                                index,
                                "lastName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            error={!!errorMessage?.lastName}
                            helperText={errorMessage?.lastName}
                            value={value?.lastName}
                          />
                        </Grid>
                      </>
                    );

                  case 3:
                    return (
                      <>
                        <Grid item xs={8} sm={2} md={2}>
                          <TextField
                            label={getTranslation(
                              props.currentLang,
                              "surveyBuilder:questionContact.firstName"
                            )}
                            variant="outlined"
                            className={styles.oneRowNameInput}
                            inputProps={{
                              "data-testid": "firstName",
                            }}
                            InputLabelProps={{
                              required: required,
                            }}
                            onChange={(e) =>
                              onChange(
                                index,
                                "firstName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            onBlur={(e) =>
                              onChange(
                                index,
                                "firstName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            error={!!errorMessage?.firstName}
                            helperText={errorMessage?.firstName}
                            value={value?.firstName}
                          />
                        </Grid>
                        <Grid item xs={4} sm={2} md={2}>
                          <TextField
                            label={getTranslation(
                              props.currentLang,
                              "surveyBuilder:questionContact.middleName"
                            )}
                            variant="outlined"
                            className={styles.oneRowNameInput}
                            inputProps={{
                              "data-testid": "middleName",
                            }}
                            InputLabelProps={{
                              required: required,
                            }}
                            onChange={(e) =>
                              onChange(
                                index,
                                "middleName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            onBlur={(e) =>
                              onChange(
                                index,
                                "middleName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            error={!!errorMessage?.middleName}
                            helperText={errorMessage?.middleName}
                            value={value?.middleName}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2} md={2}>
                          <TextField
                            label={getTranslation(
                              props.currentLang,
                              "surveyBuilder:questionContact.lastName"
                            )}
                            variant="outlined"
                            className={styles.oneRowNameInput}
                            inputProps={{
                              "data-testid": "lastName",
                            }}
                            InputLabelProps={{
                              required: required,
                            }}
                            onChange={(e) =>
                              onChange(
                                index,
                                "lastName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            onBlur={(e) =>
                              onChange(
                                index,
                                "lastName",
                                e.target.value,
                                required,
                                fieldType
                              )
                            }
                            error={!!errorMessage?.lastName}
                            helperText={errorMessage?.lastName}
                            value={value?.lastName}
                          />
                        </Grid>
                      </>
                    );
                }

                break;

              case "email":
                return (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={label}
                        variant="outlined"
                        className={styles.oneRowNameInput}
                        placeholder={label}
                        inputProps={{
                          "data-testid": "email",
                        }}
                        InputLabelProps={{
                          required: required,
                        }}
                        onChange={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        onBlur={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        error={!!errorMessage}
                        helperText={errorMessage}
                        value={value}
                        type={maskTextInput ? "password" : undefined}
                      />
                    </Grid>
                  </>
                );
              case "phone":
                return (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={label}
                        variant="outlined"
                        className={styles.oneRowNameInput}
                        placeholder={label}
                        inputProps={{
                          "data-testid": "phone",
                        }}
                        InputLabelProps={{
                          required: required,
                        }}
                        onChange={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        onBlur={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        error={!!errorMessage}
                        helperText={errorMessage}
                        value={value}
                        type={maskTextInput ? "password" : undefined}
                      />
                    </Grid>
                  </>
                );

              case "dateOfBirth":
                return (
                  <>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label={label}
                          mask={renderMask(dateFormat)}
                          inputFormat={dateFormat.replace(/mm/i, "MM")}
                          // data-testid={"dateOfBirth"}
                          InputProps={{ "data-testid": "dateOfBirth" }}
                          value={
                            value
                              ? moment(value, dateFormat.toUpperCase()).toDate()
                              : null
                          }
                          views={["year", "month", "day"]}
                          onChange={(_value) => {
                            const value = _value
                              ? moment(_value).format(dateFormat.toUpperCase())
                              : "";
                            onChange(index, null, value, required, fieldType);
                          }}
                          maxDate={new Date()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={dateFormat}
                              fullWidth
                              className={styles.oneRowNameInput}
                              InputLabelProps={{
                                required: required,
                              }}
                              error={!!errorMessage}
                              helperText={errorMessage}
                              onBlur={(e) => {
                                const value = e.target.value
                                  ? moment(e.target.value).format(
                                      dateFormat.toUpperCase()
                                    )
                                  : "";
                                onChange(
                                  index,
                                  null,
                                  value,
                                  required,
                                  fieldType
                                );
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>
                );

              case "address":
                return (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={label}
                        variant="outlined"
                        className={styles.oneRowNameInput}
                        placeholder={label}
                        inputProps={{
                          "data-testid": "address",
                        }}
                        InputLabelProps={{
                          required: required,
                        }}
                        onChange={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        onBlur={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        error={!!errorMessage}
                        helperText={errorMessage}
                        value={value}
                      />
                    </Grid>
                  </>
                );

              case "zip":
                return (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={label}
                        variant="outlined"
                        className={styles.oneRowNameInput}
                        placeholder={label}
                        inputProps={{
                          "data-testid": "zip",
                        }}
                        InputLabelProps={{
                          required: required,
                        }}
                        onChange={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        onBlur={(e) =>
                          onChange(
                            index,
                            null,
                            e.target.value,
                            required,
                            fieldType
                          )
                        }
                        error={!!errorMessage}
                        helperText={errorMessage}
                        value={value}
                      />
                    </Grid>
                  </>
                );

              case "custom":
                switch (inputType) {
                  case "textInput":
                    return (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={label}
                          variant="outlined"
                          className={styles.oneRowNameInput}
                          placeholder={label}
                          inputProps={{
                            "data-testid": `custom${index}`,
                          }}
                          InputLabelProps={{
                            required: required,
                          }}
                          onChange={(e) =>
                            onChange(
                              index,
                              null,
                              e.target.value,
                              required,
                              fieldType
                            )
                          }
                          onBlur={(e) =>
                            onChange(
                              index,
                              null,
                              e.target.value,
                              required,
                              fieldType
                            )
                          }
                          error={!!errorMessage}
                          helperText={errorMessage}
                          value={value}
                          type={maskTextInput ? "password" : undefined}
                        />
                      </Grid>
                    );

                  case "dropdown":
                    return (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            disableClearable
                            options={dropdownContent}
                            data-testid={`custom${index}`}
                            value={dropdownContent.find(
                              (option) => option.value === value
                            )}
                            onChange={(_event, option) =>
                              onChange(
                                index,
                                null,
                                option.value,
                                required,
                                fieldType
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={label}
                                variant="outlined"
                                className={styles.oneRowNameInput}
                                InputLabelProps={{
                                  required: required,
                                }}
                                onBlur={(e) =>
                                  onChange(
                                    index,
                                    null,
                                    e.target.value,
                                    required,
                                    fieldType
                                  )
                                }
                                error={!!errorMessage}
                                helperText={errorMessage}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    );
                }

                break;
            }
          })}
        </Grid>
      </div>
    </div>
  );
};

// AnswerContact.defaultProps = {
//   question: "",
//   required: false,
//   handleChange: () => {},
//   value: "",
// };

// AnswerContact.propTypes = {
//   question: PropTypes.string.isRequired,
//   required: PropTypes.bool.isRequired,
//   handleChange: PropTypes.func.isRequired,
//   value: PropTypes.string.isRequired,
//   helperText: PropTypes.string,
//   maxChar: PropTypes.number,
//   charLimitWarnMsg: PropTypes.string,
//   contactForms: PropTypes.object,
// };

export default React.forwardRef(AnswerContact);
